import {useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import {Alert, Badge, Button, Form, Modal, Spinner, Table} from 'react-bootstrap'
import Select, {MultiValue} from 'react-select'
import {defineMessages, useIntl} from 'react-intl'
import {toast} from 'react-toastify'

import {fetchNewToken, URL} from '../../../gql/client'
import {parseId} from '../../../helpers'
import {STORAGE_NAMES} from '../../../helpers/consts'

/** Separadores y marcadores de la vista previa: son diseno, no texto traducible. */
const COLON = ': '
const EM_DASH = '—'
const ARROW = ' → '
const MID_DOT = ' · '
const HASH = '#'
const PAREN_OPEN = ' ('
const PAREN_CLOSE = ')'

type Option = {id: number; name: string}
type SourceOption = Option & {category: string}
type TagOption = Option & {color: string}

type PreviewRow = {
  row_number: number
  name: string
  email: string
  phone: string
  created_time: string | null
  platform: string
  source_id: number | null
  source_name: string | null
  status: 'ready' | 'duplicate' | 'error'
  duplicate_kind: string | null
  duplicate_leads: Array<{
    id: number
    name: string
    email: string | null
    phone: string
    stage: string | null
  }>
  errors: string[]
  warnings: string[]
}

type LeadImportPreview = {
  file_name: string
  pipeline: Option
  stage: Option
  campuses: Option[]
  selected_campus_id: number | null
  requires_campus: boolean
  sources: SourceOption[]
  tags: TagOption[]
  source_mapping: Array<{
    platform: string
    source_id: number | null
    source_name: string | null
    count: number
  }>
  timezone: string
  summary: {
    total: number
    ready: number
    duplicates: number
    exact_duplicates: number
    errors: number
  }
  rows: PreviewRow[]
  preview_truncated: boolean
}

type ImportResult = {
  success: boolean
  created: number
  skipped_duplicates: number
  skipped_errors: number
  assignments: Array<{user: string; count: number}>
}

interface Props {
  isOpen: boolean
  onClose: () => void
  onImported: () => void
  pipelineId: string
  pipelineName: string
}

const TIMEZONE = 'America/New_York'

const messages = defineMessages({
  title: {id: 'leads.import.title', defaultMessage: 'Import leads'},
  file: {id: 'leads.import.file', defaultMessage: 'Excel or CSV file'},
  analyze: {id: 'leads.import.analyze', defaultMessage: 'Analyze file'},
  analyzing: {id: 'leads.import.analyzing', defaultMessage: 'Analyzing…'},
  pipeline: {id: 'leads.import.pipeline', defaultMessage: 'Pipeline'},
  stage: {id: 'leads.import.stage', defaultMessage: 'Destination stage'},
  campus: {id: 'leads.import.campus', defaultMessage: 'Campus'},
  selectCampus: {id: 'leads.import.selectCampus', defaultMessage: 'Select a Campus'},
  timezone: {id: 'leads.import.timezone', defaultMessage: 'Created time zone'},
  sourceMode: {id: 'leads.import.sourceMode', defaultMessage: 'Lead source'},
  sourcePlatform: {
    id: 'leads.import.sourcePlatform',
    defaultMessage: 'Detect from the platform column',
  },
  sourceGlobal: {
    id: 'leads.import.sourceGlobal',
    defaultMessage: 'Apply the same source to every lead',
  },
  selectSource: {id: 'leads.import.selectSource', defaultMessage: 'Select a source'},
  mappings: {id: 'leads.import.mappings', defaultMessage: 'Detected source mappings'},
  tags: {id: 'leads.import.tags', defaultMessage: 'Tags applied to every imported lead'},
  selectTags: {id: 'leads.import.selectTags', defaultMessage: 'Select tags (optional)'},
  importDuplicates: {
    id: 'leads.import.importDuplicates',
    defaultMessage: 'Import possible contact duplicates',
  },
  duplicateHint: {
    id: 'leads.import.duplicateHint',
    defaultMessage: 'Exact Meta IDs and conflicting contacts are never imported twice.',
  },
  ready: {id: 'leads.import.ready', defaultMessage: 'Ready'},
  duplicates: {id: 'leads.import.duplicates', defaultMessage: 'Possible duplicates'},
  errors: {id: 'leads.import.errors', defaultMessage: 'With errors'},
  total: {id: 'leads.import.total', defaultMessage: 'Total rows'},
  row: {id: 'leads.import.row', defaultMessage: 'Row'},
  lead: {id: 'leads.import.lead', defaultMessage: 'Lead'},
  source: {id: 'leads.import.source', defaultMessage: 'Source'},
  status: {id: 'leads.import.status', defaultMessage: 'Status'},
  details: {id: 'leads.import.details', defaultMessage: 'Details'},
  readyStatus: {id: 'leads.import.readyStatus', defaultMessage: 'Ready'},
  duplicateStatus: {id: 'leads.import.duplicateStatus', defaultMessage: 'Duplicate'},
  errorStatus: {id: 'leads.import.errorStatus', defaultMessage: 'Error'},
  truncated: {
    id: 'leads.import.truncated',
    defaultMessage: 'Only the first 100 rows are shown. The full file will be processed.',
  },
  importButton: {id: 'leads.import.importButton', defaultMessage: 'Import {count} leads'},
  importing: {id: 'leads.import.importing', defaultMessage: 'Importing…'},
  close: {id: 'common.action.close', defaultMessage: 'Close'},
  success: {
    id: 'leads.import.success',
    defaultMessage: '{count} leads were imported successfully.',
  },
  skipped: {
    id: 'leads.import.skipped',
    defaultMessage: '{duplicates} duplicates and {errors} invalid rows were skipped.',
  },
  assignments: {id: 'leads.import.assignments', defaultMessage: 'Assignment summary'},
  chooseFile: {id: 'leads.import.chooseFile', defaultMessage: 'Select a file before analyzing.'},
  chooseCampus: {id: 'leads.import.chooseCampus', defaultMessage: 'Select a Campus.'},
  chooseSource: {id: 'leads.import.chooseSource', defaultMessage: 'Select a lead source.'},
})

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem(STORAGE_NAMES.TOKEN) || ''}`,
})

const postForm = async <T,>(path: string, form: FormData) => {
  try {
    return await axios.post<T>(`${URL}${path}`, form, {headers: authHeaders()})
  } catch (error: any) {
    if (error?.response?.status === 401) {
      const token = await fetchNewToken()
      if (token) {
        return await axios.post<T>(`${URL}${path}`, form, {
          headers: {Authorization: `Bearer ${token}`},
        })
      }
    }
    throw error
  }
}

export const ModalImportLeads = ({
  isOpen,
  onClose,
  onImported,
  pipelineId,
  pipelineName,
}: Props) => {
  const intl = useIntl()
  const [file, setFile] = useState<File | null>(null)
  const [preview, setPreview] = useState<LeadImportPreview | null>(null)
  const [campusId, setCampusId] = useState('')
  const [sourceMode, setSourceMode] = useState<'platform' | 'global'>('platform')
  const [globalSourceId, setGlobalSourceId] = useState('')
  const [selectedTags, setSelectedTags] = useState<
    MultiValue<{value: string; label: string; color: string}>
  >([])
  const [importDuplicates, setImportDuplicates] = useState(false)
  const [loadingPreview, setLoadingPreview] = useState(false)
  const [loadingImport, setLoadingImport] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)

  useEffect(() => {
    if (isOpen) return
    setFile(null)
    setPreview(null)
    setCampusId('')
    setSourceMode('platform')
    setGlobalSourceId('')
    setSelectedTags([])
    setImportDuplicates(false)
    setResult(null)
  }, [isOpen])

  const selectedTagIds = useMemo(() => selectedTags.map(({value}) => value), [selectedTags])

  const buildForm = () => {
    const form = new FormData()
    if (file) form.append('file', file, file.name)
    form.append('pipeline_id', String(parseId(pipelineId)))
    if (campusId) form.append('campus_id', campusId)
    form.append('timezone', TIMEZONE)
    form.append('source_mode', sourceMode)
    if (globalSourceId) form.append('global_source_id', globalSourceId)
    form.append('tag_ids', JSON.stringify(selectedTagIds))
    return form
  }

  const errorMessage = (error: any) =>
    error?.response?.data?.error || error?.message || 'Unexpected error'

  const analyze = async () => {
    if (!file) {
      toast.warning(intl.formatMessage(messages.chooseFile))
      return
    }
    if (sourceMode === 'global' && preview && !globalSourceId) {
      toast.warning(intl.formatMessage(messages.chooseSource))
      return
    }
    setLoadingPreview(true)
    setResult(null)
    try {
      const response = await postForm<LeadImportPreview>('/api/leads/import/preview/', buildForm())
      setPreview(response.data)
      if (response.data.selected_campus_id) {
        setCampusId(String(response.data.selected_campus_id))
      }
    } catch (error: any) {
      toast.error(errorMessage(error))
    } finally {
      setLoadingPreview(false)
    }
  }

  const estimatedCount = preview
    ? preview.summary.ready +
      (importDuplicates
        ? Math.max(0, preview.summary.duplicates - preview.summary.exact_duplicates)
        : 0)
    : 0

  const commit = async () => {
    if (!file || !preview) return
    if (!campusId) {
      toast.warning(intl.formatMessage(messages.chooseCampus))
      return
    }
    if (sourceMode === 'global' && !globalSourceId) {
      toast.warning(intl.formatMessage(messages.chooseSource))
      return
    }
    setLoadingImport(true)
    try {
      const form = buildForm()
      form.append('import_duplicates', String(importDuplicates))
      const response = await postForm<ImportResult>('/api/leads/import/commit/', form)
      setResult(response.data)
      toast.success(intl.formatMessage(messages.success, {count: response.data.created}))
      onImported()
    } catch (error: any) {
      toast.error(errorMessage(error))
    } finally {
      setLoadingImport(false)
    }
  }

  const statusBadge = (row: PreviewRow) => {
    if (row.status === 'ready') {
      return <Badge bg='success'>{intl.formatMessage(messages.readyStatus)}</Badge>
    }
    if (row.status === 'duplicate') {
      return <Badge bg='warning'>{intl.formatMessage(messages.duplicateStatus)}</Badge>
    }
    return <Badge bg='danger'>{intl.formatMessage(messages.errorStatus)}</Badge>
  }

  return (
    <Modal show={isOpen} onHide={loadingImport ? undefined : onClose} size='xl' centered scrollable>
      <Modal.Header closeButton={!loadingImport}>
        <Modal.Title>{intl.formatMessage(messages.title)}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {result ? (
          <>
            <Alert variant='success'>
              <div className='fw-bold'>
                {intl.formatMessage(messages.success, {count: result.created})}
              </div>
              <div>
                {intl.formatMessage(messages.skipped, {
                  duplicates: result.skipped_duplicates,
                  errors: result.skipped_errors,
                })}
              </div>
            </Alert>
            {result.assignments.length > 0 && (
              <div>
                <h6>{intl.formatMessage(messages.assignments)}</h6>
                <div className='d-flex gap-2 flex-wrap'>
                  {result.assignments.map((assignment) => (
                    <Badge bg='light' text='dark' key={assignment.user} className='p-2'>
                      {assignment.user}
                      {COLON}
                      {assignment.count}
                    </Badge>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <>
            <div className='row g-3 mb-4'>
              <div className='col-md-6'>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(messages.pipeline)}</Form.Label>
                  <Form.Control value={pipelineName} disabled />
                </Form.Group>
              </div>
              <div className='col-md-6'>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(messages.timezone)}</Form.Label>
                  <Form.Control value='Miami (America/New_York)' disabled />
                </Form.Group>
              </div>
              <div className='col-12'>
                <Form.Group>
                  <Form.Label>{intl.formatMessage(messages.file)}</Form.Label>
                  <Form.Control
                    type='file'
                    accept='.xlsx,.csv'
                    disabled={loadingPreview}
                    onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      setFile(event.target.files?.[0] || null)
                      setPreview(null)
                      setResult(null)
                    }}
                  />
                </Form.Group>
              </div>
            </div>

            {preview && (
              <>
                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <Form.Group>
                      <Form.Label>{intl.formatMessage(messages.stage)}</Form.Label>
                      <Form.Control value={preview.stage.name} disabled />
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <Form.Group>
                      <Form.Label>{intl.formatMessage(messages.campus)}</Form.Label>
                      <Form.Select
                        value={campusId}
                        disabled={preview.campuses.length === 1}
                        onChange={(event) => setCampusId(event.target.value)}
                      >
                        <option value=''>{intl.formatMessage(messages.selectCampus)}</option>
                        {preview.campuses.map((campus) => (
                          <option value={campus.id} key={campus.id}>
                            {campus.name}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </div>
                </div>

                <div className='row g-3 mb-4'>
                  <div className='col-md-6'>
                    <Form.Group>
                      <Form.Label>{intl.formatMessage(messages.sourceMode)}</Form.Label>
                      <Form.Check
                        type='radio'
                        name='source-mode'
                        id='lead-source-platform'
                        checked={sourceMode === 'platform'}
                        label={intl.formatMessage(messages.sourcePlatform)}
                        onChange={() => setSourceMode('platform')}
                      />
                      <Form.Check
                        type='radio'
                        name='source-mode'
                        id='lead-source-global'
                        checked={sourceMode === 'global'}
                        label={intl.formatMessage(messages.sourceGlobal)}
                        onChange={() => setSourceMode('global')}
                      />
                      {sourceMode === 'global' && (
                        <Form.Select
                          className='mt-2'
                          value={globalSourceId}
                          onChange={(event) => setGlobalSourceId(event.target.value)}
                        >
                          <option value=''>{intl.formatMessage(messages.selectSource)}</option>
                          {preview.sources.map((source) => (
                            <option value={source.id} key={source.id}>
                              {source.category}
                              {ARROW}
                              {source.name}
                            </option>
                          ))}
                        </Form.Select>
                      )}
                    </Form.Group>
                  </div>
                  <div className='col-md-6'>
                    <Form.Group>
                      <Form.Label>{intl.formatMessage(messages.tags)}</Form.Label>
                      <Select
                        isMulti
                        placeholder={intl.formatMessage(messages.selectTags)}
                        value={selectedTags}
                        options={preview.tags.map((tag) => ({
                          value: String(tag.id),
                          label: tag.name,
                          color: tag.color,
                        }))}
                        onChange={setSelectedTags}
                      />
                    </Form.Group>
                  </div>
                </div>

                {sourceMode === 'platform' && (
                  <div className='mb-4'>
                    <div className='fw-semibold mb-2'>{intl.formatMessage(messages.mappings)}</div>
                    <div className='d-flex gap-2 flex-wrap'>
                      {preview.source_mapping.map((mapping) => (
                        <Badge
                          bg={mapping.source_id ? 'light' : 'danger'}
                          text={mapping.source_id ? 'dark' : undefined}
                          className='p-2'
                          key={mapping.platform}
                        >
                          {mapping.platform}
                          {ARROW}
                          {mapping.source_name || EM_DASH}
                          {PAREN_OPEN}
                          {mapping.count}
                          {PAREN_CLOSE}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className='row g-2 mb-4'>
                  {[
                    [messages.total, preview.summary.total, 'secondary'],
                    [messages.ready, preview.summary.ready, 'success'],
                    [messages.duplicates, preview.summary.duplicates, 'warning'],
                    [messages.errors, preview.summary.errors, 'danger'],
                  ].map(([label, value, variant]) => (
                    <div className='col-6 col-md-3' key={(label as any).id}>
                      <div className={`border rounded p-3 border-${variant}`}>
                        <div className='text-muted'>{intl.formatMessage(label as any)}</div>
                        <div className='fs-2 fw-bold'>{value as number}</div>
                      </div>
                    </div>
                  ))}
                </div>

                {preview.summary.duplicates > 0 && (
                  <Form.Group className='mb-4'>
                    <Form.Check
                      type='checkbox'
                      id='import-lead-duplicates'
                      checked={importDuplicates}
                      onChange={(event) => setImportDuplicates(event.target.checked)}
                      label={intl.formatMessage(messages.importDuplicates)}
                    />
                    <Form.Text muted>{intl.formatMessage(messages.duplicateHint)}</Form.Text>
                  </Form.Group>
                )}

                <Table responsive hover size='sm' className='align-middle'>
                  <thead>
                    <tr>
                      <th>{intl.formatMessage(messages.row)}</th>
                      <th>{intl.formatMessage(messages.lead)}</th>
                      <th>{intl.formatMessage(messages.source)}</th>
                      <th>{intl.formatMessage(messages.status)}</th>
                      <th>{intl.formatMessage(messages.details)}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {preview.rows.map((row) => (
                      <tr key={row.row_number}>
                        <td>{row.row_number}</td>
                        <td>
                          <div className='fw-semibold'>{row.name || EM_DASH}</div>
                          <small className='text-muted d-block'>{row.email || EM_DASH}</small>
                          <small className='text-muted'>{row.phone || EM_DASH}</small>
                        </td>
                        <td>{row.source_name || EM_DASH}</td>
                        <td>{statusBadge(row)}</td>
                        <td>
                          {row.errors.concat(row.warnings).map((message, index) => (
                            <div className='small' key={`${message}-${index}`}>
                              {message}
                            </div>
                          ))}
                          {row.duplicate_leads.map((lead) => (
                            <div className='small mb-1' key={lead.id}>
                              <div>
                                {HASH}
                                {lead.id}
                                {MID_DOT}
                                {lead.name}
                                {MID_DOT}
                                {lead.stage || EM_DASH}
                              </div>
                              <div className='text-muted'>
                                {lead.email || EM_DASH}
                                {MID_DOT}
                                {lead.phone || EM_DASH}
                              </div>
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
                {preview.preview_truncated && (
                  <Alert variant='info'>{intl.formatMessage(messages.truncated)}</Alert>
                )}
              </>
            )}
          </>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant='secondary' onClick={onClose} disabled={loadingImport}>
          {intl.formatMessage(messages.close)}
        </Button>
        {!result && (
          <>
            <Button
              variant='outline-primary'
              onClick={analyze}
              disabled={!file || loadingPreview || loadingImport}
            >
              {loadingPreview && <Spinner animation='border' size='sm' className='me-2' />}
              {intl.formatMessage(loadingPreview ? messages.analyzing : messages.analyze)}
            </Button>
            {preview && (
              <Button
                variant='primary'
                onClick={commit}
                disabled={
                  loadingImport ||
                  !campusId ||
                  estimatedCount === 0 ||
                  (sourceMode === 'global' && !globalSourceId)
                }
              >
                {loadingImport && <Spinner animation='border' size='sm' className='me-2' />}
                {intl.formatMessage(loadingImport ? messages.importing : messages.importButton, {
                  count: estimatedCount,
                })}
              </Button>
            )}
          </>
        )}
      </Modal.Footer>
    </Modal>
  )
}
